import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Avatar from '../components/Avatar';
import TarotCard from '../components/TarotCard';
import ReadingConsole from '../components/ReadingConsole';
import ReaderStage from '../components/ReaderStage';
import {
  ApiError,
  tarotApi,
  type CrisisSignal,
  type MessageRecord,
  type SessionPhase,
  type SessionRecord,
  type SideContext,
  type SpreadResponse,
} from '../lib/api';
import {
  type Persona,
  type Theme,
} from '../lib/personas';
import { ThemeBackdrop } from '../lib/themeEmblem';
import { useTheme } from '../lib/useTheme';
import { renderSimpleMarkdown } from '../lib/markdown';
import { DEMO_CTA_COPY, resolveUiLanguage } from '../lib/publicCopy';

/**
 * /session/:id — v2 對話介面（REFACTOR）。
 *
 * 邏輯：
 *   進場：GET /api/sessions/:id 載入 session + messages
 *   送出：POST /api/sessions/:id/turn → append 新訊息
 *   Paywall：response 含 paywall_triggered → 內聯 PaywallPanel（不跳頁），
 *            同時 push history 到 /session/:id/paywall（可分享 URL，但畫面不重整）
 *   完成：rounds_used >= 20 → navigate /session/:id/report
 *
 * v2 視覺重做：
 *   - Header：persona 名 · phase kicker · 進度 dots（不是 bar）
 *   - Message bubble：theme-specific（陰影 / 邊框 / 圓角各自）
 *   - 抽牌：依靠 TarotCard 既有 3D flip + delay stagger（700ms cubic-bezier，350ms stagger）
 *   - Composer：pill input + send icon（不是大塊 textarea）
 *   - PaywallPanel inline，從 composer 區「長出來」
 *
 * Mock-friendly：後端沒跑 → demo mode（client-side state machine）。
 */

const TOTAL_ROUNDS = 20;
const PAYWALL_ROUND = 14;
const SPREAD_LABELS = ['過去', '現在', '未來'] as const;

const DEMO_CARDS = [
  'the-fool',
  'the-magician',
  'the-high-priestess',
  'the-empress',
  'the-emperor',
  'the-hierophant',
  'the-lovers',
  'the-chariot',
  'strength',
  'the-hermit',
  'wheel-of-fortune',
  'justice',
  'the-hanged-man',
  'death',
  'temperance',
  'the-devil',
  'the-tower',
  'the-star',
  'the-moon',
  'the-sun',
  'judgement',
  'the-world',
];

function pickDemoCard(seed: number): string {
  return DEMO_CARDS[seed % DEMO_CARDS.length] ?? 'the-fool';
}

function defaultPersona(): Persona {
  const t = document.documentElement.getAttribute('data-theme') as Theme | null;
  if (t === 'mystic') return 'orion';
  if (t === 'minimal') return 'sage';
  return 'luna';
}

function demoAssistantReply(
  rounds_used: number,
  isPaid: boolean,
): { reply: MessageRecord; phase: SessionPhase; paywall: boolean } {
  const next = rounds_used + 1;
  let phase: SessionPhase = 1;
  let cardsDrawn: string[] | undefined;
  let bodyKey = 'demo.assistant.phase1';
  let paywall = false;

  if (next <= 7) {
    phase = 1;
    if (next === 5) {
      cardsDrawn = [pickDemoCard(next)];
      bodyKey = 'demo.assistant.phase1Card';
    }
  } else if (next <= 13) {
    phase = 2;
    bodyKey = 'demo.assistant.phase2';
    if (next === 10) {
      cardsDrawn = [pickDemoCard(next), pickDemoCard(next + 1)];
      bodyKey = 'demo.assistant.phase2Card';
    }
  } else if (next === PAYWALL_ROUND) {
    phase = 3;
    bodyKey = 'demo.assistant.paywall';
    paywall = !isPaid;
  } else if (next <= 19) {
    phase = 4;
    bodyKey = 'demo.assistant.phase4';
    if (next === 15) {
      cardsDrawn = [pickDemoCard(next), pickDemoCard(next + 1), pickDemoCard(next + 2)];
      bodyKey = 'demo.assistant.phase4Card';
    }
  } else {
    phase = 5;
    bodyKey = 'demo.assistant.phase5';
  }

  return {
    reply: {
      id: `msg_demo_${next}`,
      role: 'assistant',
      content: bodyKey,
      cards_drawn: cardsDrawn,
      created_at: Date.now() / 1000,
    },
    phase,
    paywall,
  };
}

/**
 * 把 ApiError 拆成可讀 debug payload，方便 console + 上 Sentry / 之類。
 * 後端在 500 時會吐 { error: { code, message, rid } }，把 rid 拉出來方便 grep tail。
 */
function describeApiError(e: unknown): {
  status: number | null;
  code: string | null;
  message: string;
  rid: string | null;
  raw: unknown;
} {
  if (e instanceof ApiError) {
    const payload = e.payload as
      | { error?: { code?: string; message?: string; rid?: string | null } }
      | string
      | undefined;

    if (payload && typeof payload === 'object' && 'error' in payload && payload.error) {
      return {
        status: e.status,
        code: payload.error.code ?? null,
        message: payload.error.message ?? e.message,
        rid: payload.error.rid ?? null,
        raw: payload,
      };
    }
    return {
      status: e.status,
      code: null,
      message: typeof payload === 'string' ? payload : e.message,
      rid: null,
      raw: payload,
    };
  }
  if (e instanceof Error) {
    return { status: null, code: null, message: e.message, rid: null, raw: e };
  }
  return { status: null, code: null, message: String(e), rid: null, raw: e };
}

function readOptionalContext(value: unknown): SideContext | null {
  return value && typeof value === 'object' ? (value as SideContext) : null;
}

function readOptionalCrisis(value: unknown): CrisisSignal | null {
  return value && typeof value === 'object' ? (value as CrisisSignal) : null;
}

function mergeMessageContext(message: MessageRecord, response?: { crisis?: CrisisSignal | null; side_context?: SideContext | null; side_input?: SideContext | null }) {
  return {
    crisis: readOptionalCrisis(response?.crisis ?? message.crisis),
    sideContext: readOptionalContext(response?.side_context ?? response?.side_input ?? message.side_context),
  };
}

export default function Session() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [session, setSession] = useState<SessionRecord | null>(null);
  const [messages, setMessages] = useState<MessageRecord[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isDemo] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [activeSpread, setActiveSpread] = useState<SpreadResponse | null>(null);
  const [sideContext, setSideContext] = useState<SideContext | null>(null);
  const [crisisSignal, setCrisisSignal] = useState<CrisisSignal | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // 1. 進場載入 session
  useEffect(() => {
    if (!id) return;
    let alive = true;
    setActiveSpread(null);

    (async () => {
      try {
        const data = await tarotApi.getSession(id);
        if (!alive) return;
        setSession(data);
        setMessages(data.messages ?? []);
        const latestMessageContext = [...(data.messages ?? [])].reverse()
          .map((message) => mergeMessageContext(message))
          .find((context) => context.crisis || context.sideContext);
        setCrisisSignal(latestMessageContext?.crisis ?? readOptionalCrisis((data as { crisis?: unknown }).crisis));
        setSideContext(latestMessageContext?.sideContext ?? readOptionalContext((data as { side_context?: unknown }).side_context));
        document.documentElement.setAttribute('data-theme', data.theme);
        if (!data.is_paid && data.rounds_used >= PAYWALL_ROUND) {
          setShowPaywall(true);
        }
      } catch (e) {
        if (!alive) return;
        console.warn('[session] load failed', describeApiError(e));
        setLoadError(e instanceof ApiError && e.status === 404 ? t('session.errors.notFound') : t('session.errors.connection'));
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, t]);

  // 2. 自動捲動到最新訊息
  useEffect(() => {
    const latest = messages[messages.length - 1];
    if (latest?.role === 'assistant' && (latest.cards_drawn?.length ?? 0) >= 3) {
      window.requestAnimationFrame(() => {
        const spreadReadings = document.querySelectorAll('[data-testid="spread-reading"]');
        spreadReadings[spreadReadings.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, showPaywall]);

  // 3. 完成 (20 輪) → 自動跳 report
  useEffect(() => {
    if (session && session.rounds_used >= TOTAL_ROUNDS && id) {
      const handle = window.setTimeout(() => navigate(`/session/${id}/report`), 1200);
      return () => window.clearTimeout(handle);
    }
  }, [session, id, navigate]);

  const persona = session?.persona ?? defaultPersona();
  const phase = session?.phase ?? 1;
  const rounds_used = session?.rounds_used ?? 0;
  const isPaid = session?.is_paid ?? false;

  const phaseLabel = useMemo(() => t(`session.phases.${phase}`), [phase, t]);

  async function submitMessage(rawText: string) {
    if (!rawText.trim() || !id || !session || sending) return;
    const userText = rawText.trim();
    setSending(true);

    const userMsg: MessageRecord = {
      id: `msg_local_${Date.now()}`,
      role: 'user',
      content: userText,
      created_at: Date.now() / 1000,
    };
    setMessages((prev) => [...prev, userMsg]);

    if (isDemo) {
      const next = (session.rounds_used ?? 0) + 1;
      const sim = demoAssistantReply(session.rounds_used ?? 0, session.is_paid);
      window.setTimeout(() => {
        setMessages((prev) => [...prev, sim.reply]);
        setSession((s) => (s ? { ...s, rounds_used: next, phase: sim.phase } : s));
        setSending(false);
        if (sim.paywall) {
          window.setTimeout(() => {
            setShowPaywall(true);
            window.history.pushState(null, '', `/session/${id}/paywall`);
          }, 600);
        }
      }, 700);
      return;
    }

    try {
      const resp = await tarotApi.sendTurn(id, userText);
      const enrichedMessage: MessageRecord = {
        ...resp.message,
        crisis: resp.crisis ?? resp.message.crisis,
        side_context: resp.side_context ?? resp.side_input ?? resp.message.side_context,
      };
      setMessages((prev) => [...prev, enrichedMessage]);
      const context = mergeMessageContext(enrichedMessage, resp);
      if (context.crisis) setCrisisSignal(context.crisis);
      if (context.sideContext) setSideContext(context.sideContext);
      setSession((s) =>
        s
          ? {
              ...s,
              rounds_used: resp.session.rounds_used,
              phase: resp.session.phase,
              is_paid: resp.session.is_paid,
            }
          : s,
      );
      if (resp.paywall_triggered) {
        window.setTimeout(() => {
          setShowPaywall(true);
          window.history.pushState(null, '', `/session/${id}/paywall`);
        }, 600);
      }
    } catch (e) {
      // 拆 error 內容方便 prod debug：status / code / rid 都印
      // rid 對得到 wrangler tail 的同一行 log，可以快速 grep 到 stack
      const detail = describeApiError(e);
      console.warn('[session] sendTurn failed', {
        status: detail.status,
        code: detail.code,
        message: detail.message,
        rid: detail.rid,
        raw: detail.raw,
      });
      setMessages((prev) => [
        ...prev,
        {
          id: `msg_err_${Date.now()}`,
          role: 'system',
          content: t('session.errors.send'),
          created_at: Date.now() / 1000,
        },
      ]);
    } finally {
      setSending(false);
    }
  }

  async function handleSend() {
    if (!input.trim()) return;
    const text = input;
    setInput('');
    await submitMessage(text);
  }

  async function handleUnlock(email: string) {
    if (!id) return { success: false, error: undefined };
    try {
      await tarotApi.pay(id);
    } catch (e) {
      if (e instanceof ApiError) {
        const detail = describeApiError(e);
        console.warn('[session] pay failed', {
          status: detail.status,
          code: detail.code,
          message: detail.message,
          rid: detail.rid,
        });
        return { success: false, error: t('paywall.errors.api') };
      }
      // 網路錯（後端沒跑）→ demo 放行
    }
    setShowPaywall(false);
    setSession((s) => (s ? { ...s, is_paid: true } : s));
    window.history.pushState(null, '', `/session/${id}`);
    // 加一個 confirmation system bubble，告知 email 已記下
    if (email.trim()) {
      setMessages((prev) => [
        ...prev,
        {
          id: `msg_paid_${Date.now()}`,
          role: 'system',
          content: t('paywall.confirmInline', { email: email.trim() }),
          created_at: Date.now() / 1000,
        },
      ]);
    }
    return { success: true, error: undefined };
  }

  function handleEndHere() {
    if (!id) return;
    navigate(`/session/${id}/report`);
  }

  const initialConcern = searchParams.get('concern')?.trim() ?? '';
  const latestConcern = [...messages].reverse().find((m) => m.role === 'user')?.content ?? initialConcern;
  const latestSpreadMessage = useMemo(
    () => [...messages].reverse().find((m) => m.role === 'assistant' && (m.cards_drawn?.length ?? 0) >= 3) ?? null,
    [messages],
  );
  const visibleSpreadCards = useMemo(() => {
    if (activeSpread) {
      return activeSpread.cards.map((card) => ({
        slug: card.slug,
        name: card.name,
        positionLabel: card.position_label,
      }));
    }
    return (latestSpreadMessage?.cards_drawn ?? []).slice(0, 3).map((slug, i) => ({
      slug,
      name: '',
      positionLabel: SPREAD_LABELS[i] ?? '',
    }));
  }, [activeSpread, latestSpreadMessage]);
  const hasThreeCardSpread = visibleSpreadCards.length === 3;
  const isReadingFocus =
    Boolean(id && latestConcern) &&
    !hasThreeCardSpread &&
    !showPaywall &&
    !sending;
  const shouldShowReadingConsole =
    isReadingFocus &&
    Boolean(session);

  if (loadError) {
    return (
      <main className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display text-2xl text-heading">{loadError}</h1>
        <Link
          to="/onboarding"
          className="mt-6 inline-flex items-center justify-center px-5 py-2 text-sm transition-opacity hover:opacity-90"
          style={{
            background: 'var(--btn-bg)',
            color: 'var(--btn-ink)',
            border: '1px solid var(--btn-border, transparent)',
            borderRadius: 'var(--radius-pill)',
          }}
        >
          {t('session.errors.startOver')}
        </Link>
      </main>
    );
  }

  return (
    <main className={['tarot-page-motion relative isolate flex h-screen overflow-hidden flex-col', isReadingFocus ? 'session-reading-focus' : ''].join(' ')}>
      <ThemeBackdrop theme={theme} />

      <div className="relative z-10 mx-auto flex h-screen w-full max-w-6xl flex-col px-3 pb-2 pt-2 sm:px-4 sm:pt-3">
        <SessionHeader
          persona={persona}
          theme={theme}
          phaseLabel={phaseLabel}
          rounds={rounds_used}
          isDemo={isDemo}
        />

        <div className={isReadingFocus ? 'relative z-20 mt-2 min-h-0 flex-1' : 'relative z-20 mt-2 shrink-0'}>
          <ReaderStage persona={persona} theme={theme} cards={visibleSpreadCards}>
            {shouldShowReadingConsole && id && session ? (
            <ReadingConsole
              sessionId={id}
              persona={persona}
              theme={theme}
              concern={latestConcern}
              onSpreadComplete={(spread) => {
                setActiveSpread(spread);
                const enrichedMessage: MessageRecord = {
                  ...spread.message,
                  crisis: spread.crisis ?? spread.message.crisis,
                  side_context: spread.side_context ?? spread.message.side_context,
                };
                const context = mergeMessageContext(enrichedMessage, spread);
                if (context.crisis) setCrisisSignal(context.crisis);
                if (context.sideContext) setSideContext(context.sideContext);
                setMessages((prev) => [...prev, enrichedMessage]);
              }}
            />
            ) : null}
          </ReaderStage>
          <MudFocusHud
            theme={theme}
            phase={phase}
            rounds={rounds_used}
            sideContext={sideContext}
            crisis={crisisSignal}
          />
        </div>

        {!isReadingFocus && (
          <div
            className="session-chat-panel mt-2 grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)_auto] overflow-hidden"
            data-theme={theme}
          >
          {/* Messages */}
          <section
            aria-live="polite"
            aria-label={t('session.messagesAriaLabel')}
            className="mx-auto flex min-h-0 w-full max-w-5xl flex-col gap-4 overflow-y-auto px-3 py-6 sm:px-6 lg:px-8"
          >
          {messages.length === 0 && (
            <p className="my-auto text-center text-sm italic text-muted">{t('session.empty')}</p>
          )}
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              persona={persona}
              theme={theme}
              isDemo={isDemo}
            />
          ))}
          {hasThreeCardSpread && <BusinessDemoCta theme={theme} />}
          {sending && <ReadingThoughtProcess persona={persona} />}
          {sending && <TypingIndicator persona={persona} />}
          <div ref={messagesEndRef} />
          </section>

          {/* Composer / Paywall — 同一個區塊，paywall 從這「長出來」 */}
          <div className="relative border-t border-border bg-surface/70 p-3 sm:p-4">
          {showPaywall ? (
            <InlinePaywall

              isPaid={isPaid}
              onUnlock={handleUnlock}
              onEndHere={handleEndHere}
            />
          ) : (
            <Composer
              value={input}
              onChange={setInput}
              onSend={handleSend}
              sending={sending}
              disabled={!session}
            />
          )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Header — persona · phase kicker · 進度 dots
// ---------------------------------------------------------------------------

function SessionHeader({
  persona,
  theme,
  phaseLabel,
  rounds,
  isDemo,
}: {
  persona: Persona;
  theme: Theme;
  phaseLabel: string;
  rounds: number;
  isDemo: boolean;
}) {
  const { t } = useTranslation();
  const dots = Array.from({ length: TOTAL_ROUNDS }, (_, i) => i < rounds);
  const progress = Math.min(100, Math.max(0, (rounds / TOTAL_ROUNDS) * 100));

  return (
    <header
      className="session-header flex h-11 items-center gap-3 border-b border-border bg-elevated/70 px-3 py-2 backdrop-blur sm:h-12 sm:px-4"
      data-theme={theme}
      data-testid="session-reader"
      style={{ borderRadius: 'var(--radius-card)' }}
    >
      <Avatar persona={persona} className="h-8 w-8 shrink-0 sm:h-9 sm:w-9" alt={persona} />
      <div className="min-w-0 flex-1">
        <h1 className="truncate font-display text-sm leading-tight text-heading sm:text-base">
          {t(`onboarding.picker.options.${persona}.name`)}
        </h1>
        <p data-testid="session-phase" className="truncate font-mono text-[0.55rem] uppercase tracking-[0.15em] text-accent sm:text-[0.6rem]">
          {phaseLabel}
        </p>
      </div>

      <div className="flex w-[8.5rem] flex-col items-end gap-1 sm:w-[11.5rem]">
        <span data-testid="session-progress" className="font-mono text-[0.56rem] uppercase tracking-[0.14em] text-muted sm:text-[0.6rem]">
          {t('session.progress', { used: rounds, total: TOTAL_ROUNDS })}
        </span>
        <div className="session-progress-track h-1 w-full overflow-hidden bg-border" style={{ borderRadius: 999 }} aria-hidden>
          <span
            className="session-progress-fill block h-full bg-accent transition-[width]"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="hidden items-center gap-[3px] sm:flex" aria-hidden>
          {dots.map((on, i) => (
            <span
              key={i}
              className="block h-1 w-1 rounded-full transition-colors"
              style={{
                background: on ? 'var(--color-accent)' : 'var(--color-border)',
              }}
            />
          ))}
        </div>
        {isDemo && (
          <span className="rounded-pill bg-elevated px-2 py-0.5 font-mono text-[0.55rem] uppercase tracking-[0.18em] text-accent">
            {t('session.demoBadge')}
          </span>
        )}
      </div>
    </header>
  );
}

function BusinessDemoCta({ theme }: { theme: Theme }) {
  const { i18n } = useTranslation();
  const copy = DEMO_CTA_COPY[resolveUiLanguage(i18n.resolvedLanguage ?? i18n.language)];

  return (
    <div
      data-testid="business-demo-cta"
      className="tarot-shape-spotlight-host mx-auto w-full max-w-3xl px-4 py-4 text-center"
      style={{
        borderRadius: theme === 'minimal' ? 4 : theme === 'mystic' ? 8 : 22,
        border: '1px solid var(--color-border-hi)',
        background: 'var(--color-elevated)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <p className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-accent">
        {copy.kicker}
      </p>
      <h2 className="mt-1 font-display text-2xl text-heading">
        {copy.title}
      </h2>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-muted">
        {copy.body}
      </p>
      <Link
        to="/business"
        className="tarot-shape-spotlight-host mt-4 inline-flex rounded-full bg-[#111] px-5 py-3 text-sm font-semibold text-white"
      >
        {copy.cta}
      </Link>
    </div>
  );
}

const MUD_CHAPTERS = [
  { phase: 1, title: '門廊', summary: '你剛抵達桌邊，先把問題說清楚。', options: ['靠近牌桌', '描述心事', '暫停呼吸'] },
  { phase: 2, title: '燭光', summary: '占卜師開始整理線索，讓問題往更深處走。', options: ['追問感受', '看見模式', '保留疑問'] },
  { phase: 3, title: '門檻', summary: '付費門檻或關鍵牌前，對話短暫停住。', options: ['接受停頓', '回看路徑', '準備翻牌'] },
  { phase: 4, title: '牌桌', summary: '三張牌已形成主軸，接下來只回到文字解讀。', options: ['讀過去', '讀現在', '讀未來'] },
  { phase: 5, title: '出口', summary: '這輪占卜收束成可以帶走的一段話。', options: ['保存重點', '看報告', '結束儀式'] },
] as const;

function MudFocusHud({
  theme,
  phase,
  rounds,
  sideContext,
  crisis,
}: {
  theme: Theme;
  phase: SessionPhase;
  rounds: number;
  sideContext: SideContext | null;
  crisis: CrisisSignal | null;
}) {
  const chapter = MUD_CHAPTERS.find((item) => item.phase === phase) ?? MUD_CHAPTERS[0];
  const signals = sideContext?.signals?.filter(Boolean).slice(0, 3) ?? ['問題', '牌面', '下一步'];
  const hasCrisis = Boolean(crisis?.type);

  return (
    <aside className="mud-focus-hud" data-theme={theme} aria-label="牌桌進度">
      <div className="mud-focus-hud__label">
        <span>CH {phase}</span>
        <strong>{chapter.title}</strong>
      </div>
      <div className="mud-focus-hud__runes" aria-label="進度摘要回放">
        {MUD_CHAPTERS.map((item) => {
          const active = item.phase === phase;
          const done = item.phase < phase;
          return (
            <button
              key={item.phase}
              type="button"
              className={['mud-focus-hud__rune', active ? 'is-active' : '', done ? 'is-done' : ''].join(' ')}
              aria-label={`回放${item.title}摘要`}
              title={`${item.title}：${item.summary}`}
            >
              {item.phase}
            </button>
          );
        })}
      </div>
      <div className="mud-focus-hud__signals">
        <span>R{rounds}</span>
        {signals.map((signal) => (
          <em key={signal} title={signal} aria-label={signal} />
        ))}
      </div>
      {hasCrisis && (
        <p className="mud-focus-hud__alert" data-testid="crisis-guard">
          安全暫停
        </p>
      )}
    </aside>
  );
}

function MessageContextFootnote({
  crisis,
  sideContext,
}: {
  crisis: CrisisSignal | null;
  sideContext: SideContext | null;
}) {
  const crisisText = crisis?.message || crisis?.title || (crisis?.type ? '已暫停占卜並顯示即時求助資源。' : '');
  const contextText = sideContext?.summary || sideContext?.chapter;
  const signals = sideContext?.signals?.filter(Boolean).slice(0, 3) ?? [];
  const resources = crisis?.resources?.filter((resource) => resource.label || resource.value || resource.href || resource.phone) ?? [];
  if (!crisisText && !contextText && signals.length === 0 && resources.length === 0) return null;

  return (
    <div className="mt-3 border-t border-border pt-3 text-xs leading-relaxed text-muted">
      {crisisText && (
        <p role="status" className="text-accent" data-testid="crisis-guard">
          安全提示：{crisisText}
        </p>
      )}
      {resources.map((resource) => (
        <p
          key={`${resource.label ?? ''}-${resource.value ?? resource.href ?? resource.phone ?? ''}`}
          data-testid={(resource.value ?? resource.href ?? resource.phone ?? '').includes('988') ? 'crisis-resource-988' : undefined}
        >
          {resource.label ? `${resource.label}：` : ''}
          {resource.value ?? resource.phone ?? resource.href}
        </p>
      ))}
      {contextText && <p>脈絡：{contextText}</p>}
      {signals.length > 0 && (
        <p className="font-mono text-[0.58rem] uppercase tracking-[0.14em]">
          {signals.join(' / ')}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message bubble — theme-specific chrome
// ---------------------------------------------------------------------------

function MessageBubble({
  message,
  persona,
  theme,
  isDemo,
}: {
  message: MessageRecord;
  persona: Persona;
  theme: Theme;
  isDemo: boolean;
}) {
  const { t } = useTranslation();

  const renderedText =
    isDemo && message.content.startsWith('demo.')
      ? t(message.content)
      : message.content.startsWith('paywall.confirmInline')
      ? message.content // already translated upstream when injected
      : message.content;

  if (message.role === 'system') {
    return (
      <p data-testid="message-system" className="self-center text-pretty px-4 py-2 text-center font-mono text-[0.7rem] uppercase tracking-[0.16em] text-muted">
        {renderedText}
      </p>
    );
  }

  const isUser = message.role === 'user';
  const isSpreadReading = !isUser && (message.cards_drawn?.length ?? 0) >= 3;
  const renderedContent = (
    <>
      {isUser ? (
        <p className="whitespace-pre-wrap">{renderedText}</p>
      ) : (
        <div className="space-y-3 [font-family:var(--font-reading)]">
          {renderSimpleMarkdown(renderedText)}
        </div>
      )}

      {(message.crisis || message.side_context) && (
        <MessageContextFootnote
          crisis={readOptionalCrisis(message.crisis)}
          sideContext={readOptionalContext(message.side_context)}
        />
      )}
    </>
  );

  return (
    <div className={['flex w-full gap-2', isUser ? 'justify-end' : 'justify-start'].join(' ')}>
      {!isUser && (
        <Avatar persona={persona} className="h-8 w-8 shrink-0" alt={persona} />
      )}
      <div
        data-testid={isUser ? 'message-user' : isSpreadReading ? 'spread-reading' : 'message-assistant'}
        className={[
          'tarot-shape-spotlight-host text-pretty px-4 py-3 leading-relaxed animate-[bubble-rise_320ms_ease-out]',
          isUser ? 'is-active' : '',
          isSpreadReading
            ? 'spread-reading-scroll-message max-w-full p-0 text-[0.98rem] sm:text-base'
            : 'max-w-[84%] text-[0.95rem] sm:max-w-[78%]',
        ].join(' ')}
        data-theme={isSpreadReading ? theme : undefined}
        style={isSpreadReading ? undefined : bubbleStyle(isUser, theme)}
      >
        {isSpreadReading ? (
          <ParchmentScroll theme={theme}>
            {renderedContent}
          </ParchmentScroll>
        ) : renderedContent}

        {/* 抽牌動畫 — 沿用 TarotCard 既有 3D flip，加 stagger delay */}
        {message.cards_drawn && message.cards_drawn.length > 0 && !isSpreadReading && (
          <div
            className="mt-4 flex flex-wrap justify-center gap-3 pt-4"
            style={{ borderTop: '1px solid var(--color-border)' }}
          >
            {message.cards_drawn.map((slug, i) => (
              <TarotCard
                key={`${slug}-${i}`}
                slug={slug}

                revealed
                delay={300 + i * 350}
                className="w-24 sm:w-28"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ParchmentScroll({ theme, children }: { theme: Theme; children: ReactNode }) {
  return (
    <section className="parchment-scroll" data-theme={theme} aria-label="羊皮紙解讀卷軸">
      <div className="parchment-scroll__roller parchment-scroll__roller--top" aria-hidden="true">
        <span />
      </div>
      <div className="parchment-scroll__sheet">
        <div className="parchment-scroll__seal" aria-hidden="true" />
        <div className="parchment-scroll__content">
          {children}
        </div>
      </div>
      <div className="parchment-scroll__roller parchment-scroll__roller--bottom" aria-hidden="true">
        <span />
      </div>
    </section>
  );
}

/**
 * Bubble chrome — 每 theme 各一套：
 *  - mystic   內陰金邊 + 半透深底
 *  - minimal  紙卡 + 薄邊
 *  - morandi  圓角陶器 + 柔陰影
 *
 * 直接用 themes.css 已有的 --color-bubble-* 變數，不另開一套 token。
 */
function bubbleStyle(isUser: boolean, theme: Theme): React.CSSProperties {
  const radius =
    theme === 'morandi'
      ? isUser
        ? '18px 18px 6px 18px'
        : '18px 18px 18px 6px'
      : theme === 'mystic'
      ? isUser
        ? '8px 8px 2px 8px'
        : '8px 8px 8px 2px'
      : isUser
      ? '4px 4px 2px 4px'
      : '4px 4px 4px 2px';

  if (isUser) {
    return {
      borderRadius: radius,
      background: 'var(--color-bubble-user)',
      color: 'var(--color-bubble-user-ink)',
      border: '1px solid var(--color-bubble-user, transparent)',
    };
  }

  return {
    borderRadius: radius,
    background: 'var(--color-bubble-ai)',
    color: 'var(--color-fg)',
    border: '1px solid var(--color-bubble-ai-border)',
    boxShadow:
      theme === 'mystic'
        ? '0 1px 0 rgba(201,162,74,0.04) inset, 0 6px 24px rgba(201,162,74,0.08)'
        : theme === 'morandi'
        ? '0 1px 3px rgba(138,122,111,0.08)'
        : 'none',
  };
}

function ReadingThoughtProcess({ persona }: { persona: Persona }) {
  return (
    <div className="tarot-liquid-glass tarot-glow-border mx-auto w-full max-w-md rounded-2xl px-4 py-3 text-center">
      <div className="mb-3 flex items-center justify-center gap-3">
        <div className="tarot-thinking-avatar shrink-0" role="img" aria-label={`${persona} thinking`} />
        <div className="text-left">
          <p className="font-display text-base leading-tight text-heading">占卜師正在讀取你的回覆</p>
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.16em] text-accent">
            question · cards · story
          </p>
        </div>
      </div>
      <div className="tarot-instinct-bar" aria-hidden="true" />
      <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[0.52rem] uppercase tracking-[0.12em] text-muted">
        <span className="tarot-shape-spotlight-host is-active rounded-lg border border-border bg-elevated/50 px-2 py-1">
          讀問題
        </span>
        <span className="tarot-shape-spotlight-host rounded-lg border border-border bg-elevated/50 px-2 py-1">
          對牌義
        </span>
        <span className="tarot-shape-spotlight-host rounded-lg border border-border bg-elevated/50 px-2 py-1">
          串故事
        </span>
      </div>
    </div>
  );
}

function TypingIndicator({ persona }: { persona: Persona }) {
  return (
    <div className="flex items-center gap-2 self-start">
      <Avatar persona={persona} className="h-8 w-8" alt={persona} />
      <span
        className="inline-flex gap-1 px-4 py-3"
        style={{
          background: 'var(--color-bubble-ai)',
          border: '1px solid var(--color-bubble-ai-border)',
          borderRadius: '14px 14px 14px 4px',
        }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="block h-1.5 w-1.5 rounded-full"
            style={{
              background: 'var(--color-muted)',
              animation: `tarot-typing 1.2s ${i * 0.15}s ease-in-out infinite`,
            }}
          />
        ))}
        <style>{`@keyframes tarot-typing {
          0%, 60%, 100% { transform: translateY(0); opacity: .4; }
          30% { transform: translateY(-3px); opacity: .9; }
        }`}</style>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composer — pill input + send icon
// ---------------------------------------------------------------------------

function Composer({
  value,
  onChange,
  onSend,
  sending,
  disabled = false,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  sending: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSend();
      }}
      className={[
        'tarot-shape-spotlight-host relative flex items-center gap-2 px-3 py-2',
        value.trim() ? 'is-active' : '',
      ].join(' ')}
      style={{
        background: 'var(--color-composer-bg)',
        border: '1px solid var(--color-composer-border)',
        borderRadius: 'var(--radius-pill)',
      }}
    >
      {value.trim() && (
        <span className="pointer-events-none absolute inset-x-12 bottom-1">
          <span className="tarot-instinct-bar block" aria-hidden="true" />
        </span>
      )}
      <input
        data-testid="composer-input"
        type="text"
        aria-label={t('session.composerAriaLabel')}
        placeholder={t('session.composerPlaceholder')}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // IME 中文 / 日文 / 韓文輸入法選字時 Enter 不送出（isComposing 期間）
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void onSend();
          }
        }}
        className="flex-1 bg-transparent px-3 py-2 text-sm text-fg outline-none placeholder:text-muted"
      />
      <button
        data-testid="composer-send"
        type="submit"
        disabled={disabled || sending || !value.trim()}
        aria-label={t('session.send')}
        className={[
          'tarot-shape-spotlight-host flex h-9 w-9 shrink-0 items-center justify-center transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30',
          value.trim() && !sending ? 'is-active' : '',
        ].join(' ')}
        style={{
          background: 'var(--btn-bg)',
          color: 'var(--btn-ink)',
          border: '1px solid var(--btn-border, transparent)',
          borderRadius: '50%',
          boxShadow: 'var(--btn-shadow)',
        }}
      >
        <SendIcon />
      </button>
    </form>
  );
}

function SendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M3 12l18-9-7 9 7 9-18-9zm0 0h11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// InlinePaywall — paywall 從 composer 區「長出來」
// ---------------------------------------------------------------------------

function InlinePaywall({
  isPaid,
  onUnlock,
  onEndHere,
}: {
  isPaid: boolean;
  onUnlock: (email: string) => Promise<{ success: boolean; error?: string }>;
  onEndHere: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isPaid) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (paying) return;
    setPaying(true);
    setError(null);
    const result = await onUnlock(email);
    if (!result.success && result.error) setError(result.error);
    setPaying(false);
  }

  return (
    <div
      data-testid="inline-paywall"
      className="origin-bottom animate-[paywall-grow_400ms_cubic-bezier(.4,0,.2,1)] p-5"
      style={{
        background: 'var(--color-paywall-bg)',
        border: '1px solid var(--color-paywall-border)',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-display text-xl text-heading">{t('paywall.title')}</h2>
        <span className="font-display text-2xl text-accent">{t('paywall.price')}</span>
      </div>

      <p className="mt-2 text-pretty text-sm leading-relaxed text-muted">
        {t('paywall.body')}
      </p>

      <form onSubmit={submit} className="mt-4 space-y-3">
        <div
          className="flex items-center gap-2 px-3 py-2"
          style={{
            background: 'var(--color-composer-bg)',
            border: '1px solid var(--color-composer-border)',
            borderRadius: 'var(--radius-pill)',
          }}
        >
          <EnvelopeIcon />
          <input
            data-testid="paywall-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('paywall.emailPlaceholder')}
            className="flex-1 bg-transparent px-1 py-1 text-sm text-fg outline-none placeholder:text-muted"
            aria-label={t('paywall.emailPlaceholder')}
          />
        </div>

        {error && (
          <p
            role="alert"
            className="text-pretty text-xs text-accent"
          >
            {error}
          </p>
        )}

        <button
          data-testid="paywall-submit"
          type="submit"
          disabled={paying || !email.trim()}
          className="flex w-full items-center justify-center gap-2 px-6 py-3 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            background: 'var(--btn-bg)',
            color: 'var(--btn-ink)',
            border: '1px solid var(--btn-border, transparent)',
            borderRadius: 'var(--radius-pill)',
            boxShadow: 'var(--btn-shadow)',
          }}
        >
          {paying ? t('paywall.processing') : t('paywall.cta')}
        </button>

        <p className="text-center font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted">
          {t('paywall.trust')}
        </p>

        <button
          data-testid="paywall-end-here"
          type="button"
          onClick={onEndHere}
          className="block w-full text-center text-xs text-muted underline-offset-4 hover:text-fg hover:underline"
        >
          {t('paywall.endHere')}
        </button>
      </form>

      <style>{`@keyframes paywall-grow {
        from { opacity: 0; transform: translateY(8px) scaleY(0.92); }
        to   { opacity: 1; transform: translateY(0) scaleY(1); }
      }`}</style>
    </div>
  );
}

function EnvelopeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        opacity="0.8"
      />
      <path
        d="M3 7l9 6 9-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.8"
      />
    </svg>
  );
}
